import React from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { NavigationService } from '@/services/navigationService';
import { useNavigation } from '@/contexts/NavigationContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { navItemForPath } from '@/lib/navigation-catalog';
import { permissionsForPath } from '@/components/navigation-items';

export interface LinkedDoc {
  id: string;
  /** what kind of document it is ("Purchase Order", "Bill", "Change Order", "Selection") */
  type: string;
  /** the document's visible identifier, as the entity writes it ("PO000001", "BL000001", "CO000001"), with its name when it has one */
  label: string;
  /** a short extra line (status, amount) */
  detail?: string;
  /** The entity type NavigationService knows ('purchase_order', 'bill', 'change_order', 'selection'): makes the entry a link to that document. Without it the entry is plain text. */
  entityType?: string;
}

interface LinkedDocsSectionProps {
  docs: LinkedDoc[];
  emptyText?: string;
  testId?: string;
  /** Leaving for a linked document closes the form this section sits in: when it has unsaved changes the user is asked first, and can stay. */
  hasUnsavedChanges?: () => boolean;
}

/**
 * "Linked Docs": THE one section that shows the documents a record is really linked to. Each one is a link to that document (NavigationService.linkFor: the
 * page of its type, told which record to open - Bills by their own ?bill= address), with a discreet arrow and the app's hover / focus. There is nothing to tick or
 * pick here. Bills show their purchase orders, purchase orders their bills and the change order or selection they came from, change orders and selections the
 * purchase order made from them. A document appears only when a real link exists; a new kind of document is just another `type` + `entityType`.
 *
 * A link is a RELATION, not a permission. The entries are what the caller's own reads returned (the server lists only what the database lets them read), and each one opens only when the
 * caller could open that kind of document anyway: the page of its type is ON for their role in this company and they hold its View permission (the same two checks as RoutePermissionGuard).
 * Otherwise the name stays on screen -- it is already data they read -- but it is plain text with the reason, not a link. (Not the boundary: the server and RLS refuse the document itself.)
 */
export function LinkedDocsSection({ docs, emptyText = 'No linked documents', testId = 'linked-order-info', hasUnsavedChanges }: LinkedDocsSectionProps) {
  const inRouter = useInRouterContext(); // (outside a router - a bare test of a form - the entries are plain text)
  const { status, isVisibleKey } = useNavigation();
  const { ready, canAny } = usePermissions();
  // Until the navigation matrix has loaded there is nothing to judge by (the app shell renders no page before that), so this only refuses once it HAS answered.
  const canOpen = (entityType: string, id: string): boolean => {
    const { pathname } = NavigationService.linkFor(entityType, id);
    if (status === 'loaded') {
      const item = navItemForPath(pathname);
      if (!item || !isVisibleKey(item.key)) return false;
    }
    const required = permissionsForPath(pathname);
    return !(ready && required && !canAny(required));
  };
  return (
    <div className="space-y-2">
      <Label className="font-form-label">Linked Docs</Label>
      <div role="group" aria-label="Linked Docs" className="rounded-md border border-border px-3 py-2 text-sm" data-testid={testId}>
        {docs.length === 0 ? (
          <span className="text-gray-500">{emptyText}</span>
        ) : (
          docs.map((doc) => {
            const content = (
              <>
                <span className="text-gray-500">{doc.type} · </span>{doc.label}{doc.detail ? <span className="text-gray-500"> · {doc.detail}</span> : null}
              </>
            );
            if (!doc.entityType || !inRouter) return <div key={`${doc.type}-${doc.id}`}>{content}</div>;
            if (!canOpen(doc.entityType, doc.id)) {
              return <div key={`${doc.type}-${doc.id}`} className="text-gray-600" title={`You don't have access to open this ${doc.type.toLowerCase()}`} data-testid="linked-doc-restricted">{content}</div>;
            }
            const { state, ...to } = NavigationService.linkFor(doc.entityType, doc.id);
            return (
              <Link
                key={`${doc.type}-${doc.id}`}
                to={to}
                state={state}
                onClick={(event) => { if (hasUnsavedChanges?.() && !window.confirm('You have unsaved changes. Leave and lose them?')) event.preventDefault(); }}
                className="group flex items-center justify-between gap-2 rounded px-1 -mx-1 py-0.5 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                title={`Open ${doc.type.toLowerCase()} ${doc.label}`}
              >
                <span className="min-w-0 truncate group-hover:underline">{content}</span>
                <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 group-hover:text-gray-700" aria-hidden />
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
